import { _ } from "js/vendor";
import * as geom from "js/core/utilities/geom";
import { CollectionElement } from "js/elements/base/CollectionElement";
import {
    AnimationSequenceType,
    BuildAnimationType,
    NodeType
} from "common/constants";
import {
    BulletTextNodeElement, ContentAndTextNodeElement, ContentNodeElement,
    FlexCircleNodeElement,
    NumberedTextNodeElement,
    TextInShapeNodeElement, TextNodeElement
} from "../base/NodeElement";
import ConnectorGroup from "./connectors/ConnectorGroup";
import { Ellipse } from "../../core/utilities/ellipse";
import { DegreesToRadians } from "js/core/utilities/geom";

class HubAndSpoke extends CollectionElement {
    getChildItemType(model) {
        switch (model.nodeType) {
            case NodeType.FLEX_CIRCLE:
                return FlexCircleNodeElement;
            case NodeType.BULLET_TEXT:
                return BulletTextNodeElement;
            case NodeType.TEXT:
                return TextNodeElement;
            case NodeType.CONTENT_AND_TEXT:
                return ContentAndTextNodeElement;
            case NodeType.NUMBERED_TEXT:
                return NumberedTextNodeElement;
            case NodeType.CONTENT:
                return ContentNodeElement;
            case NodeType.BOX:
            case NodeType.CAPSULE:
            case NodeType.DIAMOND:
            case NodeType.CIRCLE:
                return TextInShapeNodeElement;
            default:
                return ContentAndTextNodeElement;
        }
    }

    getNodeSelectionUIType(node) {
        if (node.id == "hub") {
            return "HubSelection";
        } else {
            return "HubSpokeSelection";
        }
    }

    getNodeRolloverUIType(model) {
        return "HubSpokeRollover";
    }

    getDefaultBuildAnimationType() {
        return BuildAnimationType.OVERLAPPING;
    }

    get maxItemCount() {
        return 9; //8 spokes + hub
    }

    get nodeType() {
        return this.model.nodeType || NodeType.CONTENT_AND_TEXT;
    }

    getAllowedNodeTypes(node) {
        if (node.id == "hub") {
            return [NodeType.CIRCLE, NodeType.BOX, NodeType.DIAMOND, NodeType.TEXT, NodeType.CONTENT];
        } else {
            return [NodeType.CONTENT_AND_TEXT, NodeType.BOX, NodeType.CIRCLE, NodeType.CAPSULE, NodeType.TEXT, NodeType.BULLET_TEXT, NodeType.NUMBERED_TEXT, NodeType.CONTENT];
        }
    }

    get hubLayout() {
        return this.model.layout || "center";
    }

    get hubNode() {
        return this.itemElements.find(node => node.id === "hub");
    }

    get hubItem() {
        return this.itemCollection.find(node => node.id === "hub");
    }

    get spokeNodes() {
        return this.itemElements.filter(node => node.id !== "hub");
    }

    get ellipseScale() {
        return (this.model.ellipse_size || 1) - 1;
    }

    get connectorDefaultProperties() {
        return {
            startDecoration: this.model.startDecoration,
            endDecoration: this.model.endDecoration,
            lineStyle: this.model.lineStyle,
            lineWeight: this.model.lineWeight
        };
    }

    _migrate_6() {
        if (this.model.label) {
            this.model.items.push({
                id: "hub",
                nodeType: "circle",
                blocks: [{
                    type: "title",
                    content: {
                        text: this.model.label.text,
                        styles: this.model.label.styles
                    }
                }]
            });
        }
    }

    _build() {
        // Migration from IconCircle variation
        if (!this.hubItem) {
            const hubModel = {
                id: "hub",
                nodeType: NodeType.CIRCLE
            };
            this.itemCollection.push(hubModel);
        } else if (!this.getAllowedNodeTypes(this.hubItem).includes(this.hubItem.nodeType)) {
            this.hubItem.nodeType = NodeType.CIRCLE;
        }

        this.buildItems();

        if (!this.model.connectors) {
            this.model.connectors = {
                items: []
            };

            for (const node of this.spokeNodes) {
                this.model.connectors.items.push({
                    ...this.defaultConnectorData,
                    target: node.id,
                });
            }
        }

        this.connectors = this.addElement("connectors", () => ConnectorGroup, {
            model: this.model.connectors,
            containerElement: this,
            startPointsAreLocked: true,
            endPointsAreLocked: true,
            canDeleteConnectors: false,
            defaultProps: () => {
                return this.connectorDefaultProperties;
            }
        });
        this.connectors.layer = -1;
    }

    get defaultItemData() {
        return {
            title: { text: "" },
            nodeType: (_.last(this.spokeNodes) || this.hubItem).nodeType
        };
    }

    get defaultConnectorData() {
        let startDecoration = "none";
        let endDecoration = "none";
        // migration from v5
        if (this.model.show_arrows == null || this.model.show_arrows !== false) {
            if (this.model.pointing_in === false) {
                endDecoration = "arrow";
            } else {
                startDecoration = "arrow";
            }
        }
        return {
            source: "hub",
            startDecoration,
            endDecoration,
            lineStyle: "solid",
            lineWeight: 2
        };
    }

    addItem(props, index) {
        const item = super.addItem(props, index);
        this.model.connectors.items.push({
            ...this.defaultConnectorData,
            id: null,
            target: item.id
        });
        return item;
    }

    deleteItem(itemId) {
        super.deleteItem(itemId);
        _.remove(this.model.connectors.items, connector => connector.target === itemId);
    }

    _calcProps(props, options) {
        const { size, children, styles } = props;

        const hub = this.hubNode;
        const hubProps = children.setProps(this.hubNode, size);

        let arcStart, arcAngle, rx, ry, flip;

        const nodeSize = new geom.Size(300, 200);

        const minRx = 150;
        const maxRx = size.width / 2 - nodeSize.width / 2;

        rx = minRx + (this.ellipseScale * (maxRx - minRx));

        switch (this.hubLayout) {
            case "center":
                if (this.model.useAlternateRotation) {
                    switch (this.spokeNodes.length) {
                        case 2:
                            arcStart = DegreesToRadians(-180);
                            break;
                        case 4:
                            arcStart = DegreesToRadians(-135);
                            break;
                        case 6:
                            arcStart = DegreesToRadians(-120);
                            break;
                        default:
                            arcStart = DegreesToRadians(-90);
                    }
                } else {
                    arcStart = DegreesToRadians(-90);
                }

                arcAngle = Math.PI * 2;
                ry = Math.min(size.height / 2, rx);
                flip = false;
                hub.model.x = size.width / 2;
                hub.model.y = size.height / 2;
                break;
            case "bottom":
                arcStart = -Math.PI;
                arcAngle = Math.PI;
                ry = Math.min(size.height, rx);
                flip = false;
                hub.model.x = size.width / 2;
                hub.model.y = size.height - hubProps.size.height / 2;
                break;
            case "top":
                arcStart = -Math.PI;
                arcAngle = Math.PI;
                ry = Math.min(size.height, rx);
                flip = true;
                hub.model.x = size.width / 2;
                hub.model.y = 0 + hubProps.size.height / 2;
                break;
        }

        hubProps.bounds = new geom.Rect(hub.model.x, hub.model.y, hubProps.size);

        const cx = hub.model.x;
        const cy = hub.model.y;

        this.layoutNodes(props, cx, cy, rx, ry, arcStart, arcAngle, flip);

        // readjust node's to ensure that they all fit
        const nodesBounds = this.itemElements.map(node => node.bounds).reduce((acc, cur) => acc.union(cur));
        const center = new geom.Point(cx, cy);
        const width = Math.max(
            center.distance(new geom.Point(nodesBounds.left, center.y)),
            center.distance(new geom.Point(nodesBounds.right, center.y))
        ) * 2;
        const height = Math.max(
            center.distance(new geom.Point(center.x, nodesBounds.top)),
            center.distance(new geom.Point(center.x, nodesBounds.bottom))
        ) * 2;

        const overflowWidth = Math.max(0, width - size.width);
        const overflowHeight = Math.max(0, height - size.height);

        if (overflowWidth > 0 || overflowHeight > 0) {
            rx -= overflowWidth / 2;
            ry -= overflowHeight / 2;
            this.layoutNodes(props, cx, cy, rx, ry, arcStart, arcAngle, flip);
        }

        children.setProps(this.connectors, size);

        return { size };
    }

    layoutNodes(props, cx, cy, rx, ry, arcStart, arcLength, flip) {
        const { children } = props;

        const nodes = this.spokeNodes;
        const ellipse = new Ellipse(cx, cy, rx, ry, false);

        const angles = [];
        let angle = arcStart;
        for (let i = 0; i < nodes.length; i++) {
            angles.push(angle);
            if (flip) {
                angle -= arcLength / nodes.length;
            } else {
                angle += arcLength / nodes.length;
            }
        }

        for (const node of nodes) {
            const angle = angles[nodes.indexOf(node)];
            const point = ellipse.point(angle);

            node.model.x = point.x;
            node.model.y = point.y;

            const nodeProps = children.setProps(node, new geom.Size(300, 200));
            nodeProps.bounds = new geom.Rect(node.model.x, node.model.y, nodeProps.size);
        }
    }

    registerAnimations(animationManager) {
        this.spokeNodes.forEach((element, idx) => {
            const elementName = `${element.buildName}-${idx + 1}`;

            const connectorsBeforeElement = element.connectorsToNode.length && element.connectorsToNode[0].endDecoration != "none";
            if (connectorsBeforeElement) {
                this.buildConnectorAnimations(animationManager, element.connectorsToNode, "-> " + elementName);
            }

            const elementSeq = animationManager.findOrCreateAnimationSequence(element.id, elementName);
            for (const animation of element.buildAnimations) {
                elementSeq.addAnimation(animation);
            }

            if (!connectorsBeforeElement) {
                this.buildConnectorAnimations(animationManager, element.connectorsToNode, "-> " + elementName);
            }
        });
    }

    buildConnectorAnimations(animationManager, connectors, name) {
        if (connectors.length) {
            const sequence = animationManager.findOrCreateAnimationSequence(name, name);
            const pathLength = _.maxBy(connectors, c => c.connectorPath.length).connectorPath.length;
            for (const connector of connectors) {
                const reverseDirection = connector.model.endDecoration === "none";
                sequence.addAnimation({
                    type: AnimationSequenceType.ANIMATE_TO_VALUE,
                    target: connector,
                    duration: duration => pathLength * 5 * (duration / 1000),
                    easing: "easeOutSine",
                    prepare: () => {
                        connector.animationState = { drawPercentage: 0, reverseDirection };
                    },
                    callback: value => {
                        connector.animationState = { drawPercentage: value, reverseDirection };
                    },
                    reset: () => {
                        connector.animationState = { drawPercentage: 1, reverseDirection };
                    },
                    startValue: 0,
                    endValue: 1,
                });
            }
        }
    }
}

export const elements = {
    HubAndSpoke
};
